import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { DEFAULT_SEO } from 'config/seo';
import { useStorefrontContext } from 'context/StorefrontContext';
import { resolveSiteName, resolveStoreMediaUrl } from 'utils/themeDisplay';
import { buildCanonicalUrl, toMetaDescription } from 'utils/seo';

interface PageMetaProps {
  title?: string;
  description?: string;
  image?: string;
  /** Query params que identifican el contenido y deben quedar en la canónica (el resto se descarta). */
  canonicalParams?: string[];
}

const PageMeta: React.FC<PageMetaProps> = ({ title, description, image, canonicalParams }) => {
  const { theme } = useStorefrontContext();
  const { pathname, search } = useLocation();
  const siteName = resolveSiteName(theme);
  const pageTitle = title ? `${title} | ${siteName}` : siteName;
  const pageDescription = toMetaDescription(
    description?.trim() || theme?.seoDescription,
    DEFAULT_SEO.descriptionMaxLength
  );
  const pageImage = resolveStoreMediaUrl(image) || resolveStoreMediaUrl(theme?.ogImageUrl) || resolveStoreMediaUrl(theme?.logoUrl);

  let canonicalUrl = buildCanonicalUrl(pathname);
  if (canonicalParams?.length) {
    const current = new URLSearchParams(search);
    const kept = new URLSearchParams();
    canonicalParams.forEach((key) => {
      const value = current.get(key);
      if (value) kept.set(key, value);
    });
    const query = kept.toString();
    if (query) canonicalUrl = `${canonicalUrl}?${query}`;
  }

  return (
    <Helmet>
      <title>{pageTitle}</title>
      {pageDescription && <meta name="description" content={pageDescription} />}
      <link rel="canonical" href={canonicalUrl} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content={DEFAULT_SEO.locale} />
      <meta property="og:type" content={DEFAULT_SEO.ogType} />
      <meta property="og:title" content={pageTitle} />
      {pageDescription && <meta property="og:description" content={pageDescription} />}
      <meta property="og:url" content={canonicalUrl} />
      {pageImage && <meta property="og:image" content={pageImage} />}
      <meta name="twitter:card" content={pageImage ? DEFAULT_SEO.twitterCard : 'summary'} />
      <meta name="twitter:title" content={pageTitle} />
      {pageDescription && <meta name="twitter:description" content={pageDescription} />}
      {pageImage && <meta name="twitter:image" content={pageImage} />}
    </Helmet>
  );
};

export default PageMeta;
